/**
 * Standard octave-band center frequencies (Hz) used throughout StudioShell.
 * All absorption coefficients and RT60 results are ordered against this
 * array so `alpha[0]` === 125 Hz, `alpha[5]` === 4 kHz.
 */
export const FREQUENCY_BANDS = [125, 250, 500, 1000, 2000, 4000] as const;

export type FrequencyBand = (typeof FREQUENCY_BANDS)[number];

/**
 * Absorption / sabins vector — one value per octave band (125–4000 Hz).
 * Tuple shape guarantees the 6-band invariant at the type level.
 *
 * Note: α can exceed 1.0 for highly absorbent materials (acoustic tile,
 * heavy curtains) due to edge-diffraction gains in reverb-room testing.
 */
export type AbsorptionSpectrum = readonly [
  alpha125: number,
  alpha250: number,
  alpha500: number,
  alpha1000: number,
  alpha2000: number,
  alpha4000: number,
];

/** The three categories of surfaces the user can skin a room with. */
export type MaterialCategory = 'walls' | 'floors' | 'ceilings';

/**
 * Material names are enumerated per category. Unioning them gives
 * `SurfaceMaterialName` — the type ultimately stored on `SurfaceMaterials`.
 */
export type WallMaterialName = 'Drywall' | 'Concrete' | 'Brick' | 'Wood panels';
export type FloorMaterialName =
  | 'Concrete'
  | 'Wood'
  | 'Carpet (thick)'
  | 'Carpet + pad'
  | 'Vinyl/linoleum';
export type CeilingMaterialName = 'Drywall' | 'Concrete' | 'Acoustic tile';

export type SurfaceMaterialName =
  | WallMaterialName
  | FloorMaterialName
  | CeilingMaterialName;

/**
 * Three.js PBR material parameters used for rendering the surface mesh.
 * Colors are stored as integer hex (0xRRGGBB) to match r3f / three usage.
 */
export interface MaterialVisual {
  color: number;
  emissive: number;
  roughness: number;
  metalness: number;
}

/**
 * One material definition. `alpha` drives the acoustics; `three` drives
 * the 3D rendering; `swatch` drives the picker UI chip.
 */
export interface Material {
  name: SurfaceMaterialName;
  category: MaterialCategory;
  alpha: AbsorptionSpectrum;
  /** Short human-readable description shown in the picker. */
  desc: string;
  three: MaterialVisual;
  /** CSS color string used for UI swatches. */
  swatch: string;
}

/**
 * Mapping from a surface to the category of materials that can dress it.
 * Keeps wall/ceiling/floor pickers honest at compile time.
 */
export const SURFACE_MATERIAL_GROUP = {
  floor: 'floors',
  ceiling: 'ceilings',
  'wall-n': 'walls',
  'wall-s': 'walls',
  'wall-e': 'walls',
  'wall-w': 'walls',
} as const satisfies Record<string, MaterialCategory>;
